import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  permissionTable,
  fieldTable,
  heading,
} from '../helpers';

export const orgAndNexaStayGuides = [
  buildGuide({
    id: 'org-hotelier',
    title: 'Hotelier (Select Property)',
    moduleId: 'organisation',
    summary: 'Choose which hotel to operate from the organisation landing page.',
    keywords: ['hotelier', 'select hotel', 'properties', 'subscription'],
    pathHint: 'Landing → Hotelier',
    related: ['org-dashboard', 'first-login', 'org-settings'],
    overview: [
      ...paragraphs(
        'Hotelier is the property picker on the organisation Landing page. It lists every hotel assigned to your user so you can enter that property’s operational PMS screens (bookings, housekeeping, POS, finance, and more).',
        'Each hotel appears as a card with photo (or placeholder), name, city/country, optional property type and star rating, and a status chip. Subscription health is visible here: Active, Onboarding, Suspended, or Expired — with a renew link when entitlement is expired or expiring within 30 days.',
        'You must open the correct hotel before creating walk-ins, taking payments, or posting POS orders. RoomNexa does not magically merge properties; everything you save applies to the hotel context you entered.',
        'Example: a relief manager covering two city hotels finishes morning checkout at Property A, returns to Landing → Hotelier, opens Property B, confirms the header name, then starts B’s arrivals. Skipping the header check is how bookings land on the wrong folio set.',
        'Benefits: clear multi-property navigation, early warning on subscription expiry, and a single place to see which hotels you are allowed to operate.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Active login',
          'You must be signed in to see Landing → Hotelier',
        ],
        [
          'Hotel assignment',
          'Admin must assign at least one hotel to your user or the list is empty',
        ],
        [
          'Valid subscription to open',
          'Expired hotels cannot be opened until entitlement is renewed',
        ],
      ]),
      bullets(
        [
          'Know which property you are scheduled to work',
          'If renew links appear, loop in the org admin before peak check-in day',
          'On shared PCs, confirm you are logged in as yourself before opening a hotel',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Wrong hotel risk',
        'Always verify the property name in the operational header after open — before the first Save of the shift.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Sign in and open Landing → Hotelier (Hotels tab on mobile shell).',
        },
        {
          text: 'Wait for the hotel cards to load. If you see an error panel, refresh or check network before assuming you have no hotels.',
        },
        {
          text: 'Scan cards for the property name, city, and status chip (Active, Onboarding, Suspended, Expired).',
        },
        {
          text: 'If a renew link shows (“Subscription expired — Renew now” or “Expires in X days”), open Settings → Subscription or notify billing/admin before guests arrive.',
        },
        {
          text: 'Click an Active (or otherwise openable) hotel card. Expired cards do not enter operations.',
        },
        {
          text: 'Confirm the hotel name in the operational header matches your shift assignment.',
        },
        {
          text: 'Open Dashboard to verify rooms/calendar load for this property.',
        },
        {
          text: 'When switching properties, return to Landing → Hotelier and open the next hotel — finish in-flight tasks in the first context first.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Hotel card', '—', 'Selects and opens the property when subscription allows'],
        ['Status chip', '—', 'Active / Onboarding / Suspended / Expired summary'],
        ['Renew link', 'When expiring/expired', 'Jumps to Settings → Subscription'],
        ['Property type / star chips', 'If configured', 'Quick identity of the asset'],
        ['Location bar', 'If city/country set', 'Helps distinguish similarly named hotels'],
        ['Open row', 'When not expired', 'Visual cue that the hotel can be entered'],
      ]),
      bullets(
        [
          'Hero image — primary hotel photo from configuration, or “No photo” placeholder',
          'Empty state — shown when no hotels are assigned or loading failed',
          'Breadcrumbs — Home / Hotelier with link to Organisation Dashboard',
        ],
        'UI elements'
      ),
    ],
    workflow: [
      workflow('Multi-property manager', [
        'Login',
        'Open Hotelier',
        'Check subscription chips',
        'Open Hotel A',
        'Confirm header',
        'Finish A tasks',
        'Return to Hotelier',
        'Open Hotel B',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Hotelier only lists hotels your user can access. Opening a hotel sets the selected property context for subsequent API calls and screens. Bookings, payments, and reports created after open apply to that hotel until you switch.',
        'Subscription state is enforced at the card: expired entitlements disable open so staff cannot operate a locked property. Expiring-soon links exist to push renewals before check-in day surprises.',
        'Onboarding and suspended statuses signal that the property may be incomplete or intentionally offline. Treat them carefully before taking live money or OTA allotments.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Say the name',
        'Say the hotel name aloud when you open it — especially on relief shifts.'
      ),
      callout(
        'tip',
        'Renew early',
        'If the renew link appears, escalate the same day. Do not wait until the card turns Expired on a full house.'
      ),
      callout(
        'note',
        'Photo missing?',
        'Upload images under hotel Configuration → Images so Hotelier cards are easier to recognise.'
      ),
    ],
    bestPractices: [
      bullets([
        'Pin a mental “home hotel” for your shift and re-check after any Landing visit.',
        'Resolve subscription warnings with org admin during weekday mornings, not Saturday arrivals.',
        'Keep similarly named properties distinguished by city on the card — confirm before open.',
        'Log out on shared PCs so the next agent does not inherit your hotel session casually.',
        'Use Organisation Dashboard for group comparisons; use Hotelier only to enter a property.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Posting a booking then realising wrong property.',
        'Ignoring “Expires in N days” until the hotel locks.',
        'Trying to open an Expired card repeatedly instead of renewing.',
        'Leaving Hotel A workflows half-saved while jumping to Hotel B.',
        'Assuming a missing hotel is a bug when the user was never assigned.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Hotel missing',
            'User not assigned',
            'Ask admin to assign hotels in Settings → Users',
          ],
          [
            'Cannot open hotel',
            'Subscription expired or suspended',
            'Check status chip; renew in Settings → Subscription',
          ],
          [
            'Wrong data after open',
            'Opened incorrect card',
            'Return to Hotelier; open the right property; verify header',
          ],
          [
            'Empty Hotelier',
            'No assignments or load error',
            'Refresh; confirm network; ask admin for assignments',
          ],
          [
            'Renew link goes nowhere useful',
            'User lacks settings access',
            'Ask org admin / billing owner to open Subscription tab',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I work two hotels at once?',
          a: 'Prefer finishing one context first. Separate browser profiles are risky; wrong-tab saves cause cross-property errors.',
        },
        {
          q: 'Why is a hotel greyed / not clickable?',
          a: 'Usually subscription expired. Renew entitlement, then reopen Hotelier.',
        },
        {
          q: 'Who assigns hotels to me?',
          a: 'An organisation admin with user management access on Landing → Settings → Users.',
        },
        {
          q: 'Is Hotelier the same as Organisation Dashboard?',
          a: 'No. Hotelier selects a property to operate. Organisation Dashboard aggregates analytics across hotels.',
        },
        {
          q: 'What does Onboarding mean?',
          a: 'The property may still be in setup. Configure masters before heavy live selling; follow your activation process to Active.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'org-dashboard',
    title: 'Organisation Dashboard',
    moduleId: 'organisation',
    summary: 'Multi-hotel performance: revenue, expenses, occupancy, and sources.',
    keywords: ['organisation dashboard', 'multi hotel', 'group performance'],
    pathHint: 'Landing → Dashboard',
    related: ['org-hotelier', 'performance-dashboard'],
    overview: [
      ...paragraphs(
        'Organisation Dashboard (Landing → Dashboard) is the multi-hotel performance board for owners, cluster GMs, and group operators. It aggregates the same style of performance intelligence you see on a property Performance Dashboard — but across the hotels you select.',
        'Use it when you need group answers: Which property is lagging on occupancy this month? Where did revenue concentrate? Are cancellations or OTA mix unhealthy at one asset? Then drill into that hotel’s Performance Dashboard and operational modules for action.',
        'The screen defaults to the current month. You choose one or many hotels from Select Hotels, set a date range, and review KPI cards plus charts for revenue vs expenses and hotel due, payment methods, booking status, sources, and inventory (direct/OTA/overbooking).',
        'Example: a three-hotel owner opens Organisation Dashboard, filters to all city hotels MTD, sees Hotel C occupancy soft while expenses high, exports PDF for the weekly call, then opens Hotel C from Hotelier to inspect Performance and Booking List.',
        'This is not a front-desk check-in board. Receptionists should live on Home Dashboard inside a hotel. Organisation Dashboard is for portfolio steering and owner reporting.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'common.view_organization_dashboard',
          'Required to open Organisation Dashboard and related landing analytics entry points',
        ],
        [
          'Hotel access scope',
          'You only see hotels assigned to you / included in the org statistics response',
        ],
      ]),
      bullets(
        [
          'Know whether you are reviewing one cluster, all hotels, or a single asset',
          'Pick a date range that matches your meeting (MTD, last month, event window)',
          'Allow browser pop-ups if you will export PDF',
          'Have property GMs ready to explain outliers — charts alone do not fix operations',
        ],
        'Prerequisites'
      ),
      callout(
        'important',
        'Not the same as hotel Performance',
        'Organisation Dashboard rolls up selected hotels on Landing. Hotel → Performance Dashboard is single-property only. Do not present them as interchangeable in owner packs.'
      ),
      callout(
        'warning',
        'Compare like with like',
        'A 20-room boutique and a 120-room resort will distort group averages. Filter to comparable hotels when coaching a GM.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'From Landing, open Dashboard (Organisation Dashboard / Multi Hotel Performance). Confirm you are not inside a single hotel’s Performance page.',
        },
        {
          text: 'Wait for hotels to load into the Select Hotels control. By default the selection covers the hotels available to you.',
        },
        {
          text: 'Adjust Select Hotels: tick specific properties or keep the full set. The chip label shows how many hotels are included.',
        },
        {
          text: 'Set start and end dates. Invalid ranges (start after end) will not fetch meaningful stats — correct them before reading KPIs.',
        },
        {
          text: 'Review top finance KPIs: Total Revenue, Hotel expenses, Hotel Due, Total Profit.',
        },
        {
          text: 'Review operations KPIs: Total Bookings / Cancelled, Booked Room / Total Room, Occupancy Percentage, Total Customers.',
        },
        {
          text: 'Inspect charts: revenue with hotel purchases and checkout due, payment methods, bookings by status over time, booking sources, and inventory (direct/OTA/overbooking).',
        },
        {
          text: 'Identify outliers. Open Hotelier → that property → Performance Dashboard or Booking List to explain the spike with line-level data.',
        },
        {
          text: 'Export PDF when you need an ownership pack. Filename includes selected hotels label and date range — keep files organised.',
        },
        {
          text: 'Share 2–3 actions with property GMs (rate, staffing, channel, expense control) and re-check the same filter next week.',
        },
      ]),
    ],
    screens: [
      heading('Filters & export'),
      fieldTable([
        [
          'Select Hotels',
          'Yes (multi-select)',
          'Limits aggregation to the ticked properties; chip shows selection summary',
        ],
        [
          'Start / end date',
          'Yes',
          'Defines the statistics window (default often current month)',
        ],
        [
          'PDF export',
          'No',
          'Builds a multi-page organisation performance PDF via on-screen capture + tables',
        ],
      ]),
      heading('KPI cards'),
      table(
        ['KPI card', 'What it shows', 'How managers use it'],
        [
          ['Total Revenue', 'Aggregated revenue for selected hotels/range', 'Group top-line health'],
          ['Hotel expenses', 'Hotel-side expenses/purchases', 'Cost control vs rooms business'],
          ['Hotel Due', 'Outstanding balance on checked-out and partial-checkout bookings', 'Follow-up on unpaid checkout balances'],
          ['Total Profit', 'Revenue minus expenses as computed', 'Portfolio profitability snapshot'],
          ['Total Bookings / Cancelled', 'Booking volume vs cancellations', 'Demand quality / policy issues'],
          ['Booked Room / Total Room', 'Rooms sold vs inventory capacity', 'Absolute occupancy load'],
          ['Occupancy Percentage', 'Occupancy rate across selected inventory', 'Compare properties fairly with care'],
          ['Total Customers', 'Customer count metric for the range', 'CRM/growth context'],
        ],
        'Organisation KPI reference'
      ),
      bullets(
        [
          'Revenue vs expenses chart — trend of total revenue with hotel and restaurant expense series',
          'Payment methods — collection mix for the selected range',
          'Bookings by status — status split over time',
          'Booking sources — direct vs channel contribution',
          'Inventory chart — direct, OTA, and overbooking indicators by date',
          'Restaurant sales split — restaurant, room service, pickup/delivery when POS data exists',
          'Breadcrumbs / nav — links toward Hotelier for drill-down',
        ],
        'Charts & chrome'
      ),
    ],
    workflow: [
      workflow('Owner weekly review', [
        'Open Landing Dashboard',
        'Select hotels (comparable set)',
        'Set MTD or last-week range',
        'Read profit & occupancy KPIs',
        'Spot lagging property',
        'Export PDF',
        'Open that hotel Performance',
        'Agree actions with GM',
        'Track next week',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Organisation statistics aggregate permitted hotels for the selected IDs and date range. If you select a subset, KPIs and charts recompute for that subset only — the full hotel list remains available in the selector so you can widen the view again.',
        'Inventory widgets use room capacity logic that can surface overbooking when direct + online booked volume exceeds capacity for a day. Treat overbooking indicators as investigation prompts (allotment, mapping, or operational oversell), not as decoration.',
        'Same-day POS posts, late checkouts, and cancellations continue to move MTD aggregates. For board meetings, export PDF at an agreed cutoff time so numbers do not shift mid-presentation.',
        'Permission common.view_organization_dashboard gates access. Users without it should not expect this Landing view; property staff may still use hotel Performance if separately permitted. Missing hotels in the selector usually mean assignment scope — not a chart bug.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Apples to apples',
        'Compare similar-sized hotels in one pass; review large resorts in a separate selection.'
      ),
      callout(
        'tip',
        'PDF for the board',
        'Export after selecting the exact hotel set and dates you will discuss — mismatched PDFs cause false alarms.'
      ),
      callout(
        'note',
        'Drill down always',
        'Group totals hide property truth. Every red flag should end in Hotelier → that hotel’s operational or Performance screens.'
      ),
    ],
    bestPractices: [
      bullets([
        'Pair Organisation Dashboard with per-hotel Performance and Reports for action, not vanity totals.',
        'Keep a recurring weekly filter (same hotels + comparable date length).',
        'Separate leisure boutiques from large commercial hotels when reviewing occupancy %.',
        'Investigate cancellation spikes by property before changing group-wide policy.',
        'Limit Organisation Dashboard access to owners/managers who can act on the insights.',
        'Store exported PDFs with hotel-set + date range in the filename for auditability.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Acting on group totals without property context.',
        'Mixing Organisation Dashboard slides with a single hotel’s Performance numbers.',
        'Leaving one tiny hotel selected and thinking the whole portfolio collapsed.',
        'Ignoring restaurant expense lines when POS-heavy properties drag profit.',
        'Reading occupancy without noting different inventory sizes across hotels.',
        'Skipping PDF pop-up permissions and assuming export is unavailable.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Empty / zero KPIs',
            'No data in range, no hotels selected usefully, or permission/scope',
            'Widen dates; select hotels; confirm view_organization_dashboard and assignments',
          ],
          [
            'Hotel missing from selector',
            'Not assigned / not returned in hotels list',
            'Ask admin to assign hotel access',
          ],
          [
            'PDF export fails',
            'Pop-up blocker or export already in progress',
            'Allow pop-ups; wait for current export; retry',
          ],
          [
            'Numbers disagree with hotel Performance',
            'Different hotel set or date range',
            'Match filters exactly; compare one hotel selection vs that hotel’s Performance',
          ],
          [
            'Overbooking looks wrong',
            'Capacity vs booked split logic / channel volume',
            'Drill into property inventory & Distribution mapping for those dates',
          ],
          [
            'Dashboard menu missing',
            'Role lacks common.view_organization_dashboard',
            'Admin grants organisation dashboard permission',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is this the same as hotel Performance Dashboard?',
          a: 'No. Organisation Dashboard is multi-hotel on Landing. Performance Dashboard is single property after you open a hotel.',
        },
        {
          q: 'Can front desk use this instead of Home Dashboard?',
          a: 'They should not. Home Dashboard is for calendar, Quick Book, and Pending Tasks. Organisation Dashboard is portfolio analytics.',
        },
        {
          q: 'Why do I only see some hotels?',
          a: 'Your user assignment and org statistics scope limit the selector. Ask admin if a property is missing.',
        },
        {
          q: 'Do I need to open a hotel first?',
          a: 'No. Organisation Dashboard lives on Landing. Open a hotel only when you drill down to operate or view property Performance.',
        },
        {
          q: 'What permission code is checked?',
          a: 'common.view_organization_dashboard (shown in product as organisation dashboard access).',
        },
        {
          q: 'Can I edit bookings from here?',
          a: 'No. Charts are read-only. Use Hotelier → hotel → Booking List / Detail to change reservations.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'org-settings',
    title: 'Organisation Settings & Subscription',
    moduleId: 'organisation',
    summary: 'Org settings including subscription/entitlement status for hotels.',
    keywords: ['subscription', 'organisation settings', 'entitlements'],
    pathHint: 'Landing → Settings',
    related: ['users-roles', 'org-hotelier'],
    overview: [
      ...paragraphs(
        'Organisation Settings on Landing is the admin home for subscription health and (when permitted) organisation user management. The Subscription tab summarises how many hotels are active, expired, upcoming, or not configured — and lists each hotel’s package names, status label, and expiry.',
        'Expired hotels are disabled on Hotelier and cannot be opened until entitlement is renewed. That makes Settings a business-continuity screen, not a once-a-year formality.',
        'The Users tab embeds organisation user administration for admins with manage users rights. Staff without that permission still use Subscription to understand why a hotel card will not open.',
        'Example: two weeks before renewal, the owner opens Settings → Subscription, sees one resort “Expires in 12 days”, coordinates billing, confirms packages (POS/Distribution) remain entitled, then briefs GMs so nobody discovers a lockout on Saturday arrivals.',
        'Package modules such as POS, Distribution, and Dynamic Pricing depend on both subscription packages and user roles — Settings clarifies the entitlement side.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Org admin / settings access',
          'Required to work in Landing → Settings productively',
        ],
        [
          'manage_users (Users tab)',
          'Needed to create/edit users and roles from Settings → Users',
        ],
        [
          'Billing contact authority',
          'Renewals often need someone who can speak to RoomNexa/billing',
        ],
      ]),
      bullets(
        [
          'List of hotels that must remain online for trading',
          'Calendar reminder 30 days before each major expiry',
          'Backup admin who can open Settings if the primary owner is travelling',
        ],
        'Prerequisites'
      ),
      callout(
        'important',
        'Renew early',
        'Do not wait for check-in day to discover an expired subscription. Expired hotels cannot be opened from Hotelier.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Landing → Settings. Choose the Subscription or Users tab (default depends on whether you can manage users).',
        },
        {
          text: 'On Subscription, read summary tiles: total hotels, active, expired, upcoming, not configured.',
        },
        {
          text: 'Scroll Hotel Subscription Status. For each hotel note status label, package names, and expiry timestamp.',
        },
        {
          text: 'Prioritise expired and upcoming rows. Coordinate renewal with RoomNexa/billing before entitlement lapses.',
        },
        {
          text: 'Confirm packages still include modules you rely on (for example POS, Distribution). Role permissions alone cannot enable a package that is not entitled.',
        },
        {
          text: 'After renewal, return to Hotelier and verify previously locked hotels open and status chips update.',
        },
        {
          text: 'On Users tab (if permitted), create/edit users, assign roles, and link hotels — see Users, Roles & Permissions guide.',
        },
        {
          text: 'Inform property GMs when a renewal completes or when a hotel will be intentionally offline.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Users tab', 'Admin only', 'Organisation user management embedded view'],
        ['Subscription tab', 'Settings access', 'Entitlement health for all hotels'],
        ['Summary tiles', '—', 'Counts: total / active / expired / upcoming / not configured'],
        ['Hotel status rows', '—', 'Per-hotel status, packages, expiry'],
        ['Permission denied alert', 'When lacking manage users', 'Explains Users tab is blocked'],
      ]),
      bullets(
        [
          'Status tones — visual emphasis for expired vs healthy entitlements',
          'Package name chips — which feature packages the hotel carries',
          'Deep link from Hotelier renew — lands on Settings with subscription tab',
        ],
        'UI elements'
      ),
    ],
    workflow: [
      workflow('Renewal', [
        'Open Settings → Subscription',
        'Identify expired/upcoming',
        'Contact RoomNexa/billing',
        'Confirm packages restored',
        'Verify Hotelier opens',
        'Inform GMs',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Subscription/entitlement state controls whether a hotel can be opened from Hotelier. Expired entitlements block operational entry even if users still have roles.',
        'Feature packages on the hotel interact with RBAC: a user may have manage_pos_restaurant, but POS stays hidden if the hotel package does not enable it. Settings is where admins verify the entitlement half of that equation.',
        'Users tab changes affect login and hotel assignment immediately for practical purposes — always use least privilege and review after staffing changes.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Two owners of billing',
        'Keep at least two people who know how to reach billing support — holidays should not lock the PMS.'
      ),
      callout(
        'tip',
        'Hotelier renew link',
        'Staff who see renew on a hotel card can send you straight to this Subscription tab — treat that ping seriously.'
      ),
      callout(
        'note',
        'Not configured',
        '“Not configured” hotels need entitlement setup before go-live trading — do not market rooms online until status is healthy.'
      ),
    ],
    bestPractices: [
      bullets([
        'Calendar reminder 30 days before each renewal.',
        'Document package modules expected per hotel (FO-only vs FO+POS+Distribution).',
        'After renewal, smoke-test open hotel + one booking screen + one report.',
        'Separate user administration duties from day-to-day FO logins.',
        'Export or screenshot subscription status before large festival weekends.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Only one person knows billing contacts.',
        'Assuming a missing POS menu is always a role bug — package may be off.',
        'Renewing after the hotel already blocked Saturday arrivals.',
        'Giving every manager full org Settings access casually.',
        'Ignoring “upcoming” until it becomes “expired”.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Hotel locked on Hotelier',
            'Subscription expired',
            'Check Subscription tab; renew; re-open Hotelier',
          ],
          [
            'Users tab blocked',
            'Missing manage_users',
            'Ask an org admin with user management rights',
          ],
          [
            'Module still missing after renewal',
            'Package not included or role lacks permission',
            'Verify package names + role permissions together',
          ],
          [
            'Status still expired after payment',
            'Entitlement not yet updated',
            'Refresh Settings; contact support with hotel name and payment ref',
          ],
          [
            'Wrong hotel listed expired',
            'Looking at similarly named property',
            'Match legal hotel name/city before escalating',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Do packages control modules?',
          a: 'Yes. POS, Distribution, Dynamic Pricing and similar features may depend on package — Settings/support clarify what you bought; roles still gate who can use them.',
        },
        {
          q: 'Can FO staff renew subscriptions?',
          a: 'Usually no. They can see renew prompts on Hotelier and escalate to org admin/billing.',
        },
        {
          q: 'Is Settings the same as hotel Configuration?',
          a: 'No. Settings here is organisation subscription/users. Property masters (rooms, rates) live under hotel Configuration after open.',
        },
        {
          q: 'What does Upcoming mean?',
          a: 'Entitlement is approaching expiry — renew before it becomes Expired and blocks Hotelier open.',
        },
        {
          q: 'Where do I manage roles?',
          a: 'From Settings → Users when role management is enabled for your org admin, or via the Users, Roles & Permissions guide path.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'users-roles',
    title: 'Users, Roles & Permissions',
    moduleId: 'organisation',
    summary: 'Create users and assign roles that gate RoomNexa modules safely.',
    keywords: ['roles', 'permissions', 'rbac', 'access control', 'users'],
    pathHint: 'Landing → Settings → Users (and hotel Users)',
    related: ['hotel-users', 'first-login', 'org-settings'],
    overview: [
      ...paragraphs(
        'RoomNexa uses role-based access control (RBAC). Roles bundle permissions such as manage_booking_operations, manage_housekeeping, manage_pos_restaurant, manage_finance, view_reports, manage_configuration, manage_distribution, manage_dynamic_pricing, and manage_users.',
        'Admins create users, assign one or more roles, and link hotels. Menus and API actions then appear only when both the role permission and (for package-scoped features) the hotel entitlement allow them.',
        'Good RBAC prevents costly mistakes: cashiers without silent refund powers, HK without rate edit rights, and FO without tax configuration. Bad RBAC — copying an owner role to everyone — makes audits and fraud investigation nearly impossible.',
        'Example: a new night auditor gets booking operations + finance view/cash permissions, but not manage_configuration or manage_users. They can close the night, not rewrite GST masters.',
        'Organisation Users (Landing Settings) and hotel Users (Configuration) both exist in the product family — use org-level admin for cross-property staff and hotel Users for property-scoped staffing as your deployment practices.'
      ),
      table(
        ['Example permission', 'Typical audience'],
        [
          ['manage_booking_operations', 'Front office'],
          ['manage_housekeeping', 'HK supervisor'],
          ['manage_pos_restaurant', 'F&B'],
          ['manage_finance / view_reports', 'Cashier / accounts / managers'],
          ['manage_configuration', 'Property manager'],
          ['manage_distribution / manage_dynamic_pricing', 'Revenue'],
          ['view_organization_dashboard', 'Owners / group managers'],
          ['manage_users / manage_roles', 'Admins'],
        ]
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        ['manage_users / manage_roles', 'Admin required to create users and design roles'],
        [
          'Hotel assignment plan',
          'Know which properties each person may open on Hotelier',
        ],
      ]),
      bullets(
        [
          'Job list for the property (FO, HK, cashier, F&B, manager)',
          'Official staff emails for password reset',
          'Least-privilege matrix agreed with GM',
          'Break-glass admin credential stored offline',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Never share owner logins',
        'Shared admin accounts destroy audit trails for refunds, rate changes, and user edits.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Landing → Settings → Users (or hotel Users under Configuration when working property-scoped).',
        },
        {
          text: 'Review existing roles. Prefer editing a role’s permission set over cloning owner access.',
        },
        {
          text: 'Create a role from the permissions catalog with least privilege for a job (for example “Front Office”).',
        },
        {
          text: 'Create a user with name, email/username, and active status.',
        },
        {
          text: 'Assign the role(s) and link the correct hotel(s).',
        },
        {
          text: 'Have the user complete First Login, change password, and open Hotelier.',
        },
        {
          text: 'Verify they see only intended menus (Booking yes, Configuration no, etc.).',
        },
        {
          text: 'Use RBAC audit views where available to confirm role assignment events.',
        },
        {
          text: 'Deactivate or remove roles promptly when staff leave; do not leave dormant accounts active.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'User list — search, sort, pagination, active flags',
          'User drawer/dialog — create/edit profile, roles, hotels',
          'Role management — permissions catalog, create/update/delete roles (when enabled)',
          'RBAC audit — role_created/updated/deleted and user role assignment events',
          'Copy / visibility actions — help admins verify what was assigned',
        ],
        'Admin UI elements'
      ),
      fieldTable([
        ['Name / email', 'Yes', 'Identity and password-reset channel'],
        ['Roles', 'Yes', 'Bundles of permissions'],
        ['Hotels', 'Yes for operators', 'Which Hotelier cards they can open'],
        ['Active flag', 'Yes', 'Inactive users should not login'],
        ['Permission checkboxes', 'Admin', 'Fine-grained common.* feature codes'],
      ]),
    ],
    workflow: [
      workflow('Role design', [
        'List jobs',
        'Map permissions',
        'Create roles',
        'Create users',
        'Assign hotels',
        'Verify menus',
        'Review quarterly',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Missing menus usually mean missing permissions or disabled hotel feature packages — not product bugs. hasFeaturePermission checks both the user permission codes and hotel-enabled features for scoped modules.',
        'Audit events (role created/updated/deleted, user roles assigned/updated/removed) help investigate “who gave refund power last Thursday?”. Use them after incidents.',
        'Least privilege is a business control: separate FO booking rights from refund/rate override and from configuration/tax masters. Quarterly access reviews catch role creep.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Break-glass admin',
        'Keep one break-glass admin account secured offline for emergencies.'
      ),
      callout(
        'tip',
        'Test with a spare login',
        'After designing a role, login as a test user before rolling it to the whole front desk.'
      ),
      callout(
        'note',
        'Hotel Users guide',
        'Property-scoped user screens are also documented under Configuration → hotel-users — keep org and hotel practices consistent.'
      ),
    ],
    bestPractices: [
      bullets([
        'Separate FO and refund powers.',
        'Review access every quarter and after every resignation.',
        'Name roles after jobs (“HK Supervisor”), not people (“Ravi full”).',
        'Require unique emails so Forgot Password works.',
        'Document who may grant manage_users — keep that circle tiny.',
        'Align package entitlements with roles when enabling POS/Distribution.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Copying owner role to every staff member.',
        'Leaving ex-employees active “just in case”.',
        'Assigning all hotels to trainees.',
        'Fixing “missing menu” by granting * permissions casually.',
        'Skipping verification login after role changes.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Module hidden',
            'Permission missing or package off',
            'Add permission to role; verify hotel package in Settings → Subscription',
          ],
          [
            'Too much access',
            'Over-broad role',
            'Create tighter role and reassign',
          ],
          [
            'User cannot see hotel',
            'Hotel not assigned',
            'Edit user hotels; confirm on Hotelier',
          ],
          [
            'Cannot open Users tab',
            'Missing manage_users',
            'Ask an org admin',
          ],
          [
            'Audit unclear',
            'Shared admin login used',
            'Move to named admins; review future RBAC events',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Custom roles?',
          a: 'Where role management allows, craft roles from the permissions catalog instead of sharing logins.',
        },
        {
          q: 'Why does my colleague see POS and I do not?',
          a: 'Different role permissions and/or hotel package entitlements. Compare both.',
        },
        {
          q: 'Are permissions the same as subscription?',
          a: 'No. Permissions gate users; subscription packages gate hotel features. You need both for package-scoped modules.',
        },
        {
          q: 'Can one user have multiple roles?',
          a: 'Yes when the admin UI allows multiple role assignment — still prefer clear job-based roles over stacking everything.',
        },
        {
          q: 'Where should I create a multi-hotel GM?',
          a: 'Org Users with assignments to each hotel plus view_organization_dashboard if they should see Landing analytics.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'nexastay-guest',
    title: 'NexaStay Guest Experience',
    moduleId: 'nexastay',
    summary: 'How guests book on the public NexaStay site: search, checkout, profile, and bookings.',
    keywords: ['nexastay', 'guest booking', 'direct website', 'checkout'],
    pathHint: 'Public NexaStay pages',
    related: ['nexastay-setup', 'reservation', 'manage-rate'],
    overview: [
      ...paragraphs(
        'NexaStay is RoomNexa’s guest-facing direct booking engine. Guests open your hotel landing page, search dates, choose a room/rate, check out (and pay when prepaid is required), then manage profile/bookings according to policy.',
        'For the hotel, successful NexaStay checkouts become PMS reservations that front office must honour — including prepaid flags, guest details, and cancellation rules configured for direct channel.',
        'Staff do not “operate” NexaStay like a POS screen; they prepare it via Distribution → NexaStay setup, Manage Rate, photos, and terms — then recognise NexaStay as a booking source at the desk.',
        'Example: a guest books Deluxe CP for Friday on your NexaStay site, pays online, receives confirmation. Friday morning FO finds the reservation in Booking List/Dashboard, verifies prepaid, assigns a clean room, and checks in without re-collecting the full stay amount.',
        'NexaStay is rooms direct booking — not QR restaurant ordering. QR ordering lives under POS.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Distribution / NexaStay setup access',
          'Managers configure the public site; FO needs booking permissions to serve arrivals',
        ],
      ]),
      bullets(
        [
          'NexaStay configured in Distribution',
          'Rates and photos live for sell dates',
          'Payments/gateway configured if you collect prepaid',
          'Terms, about, and contact pages updated',
          'FO trained to recognise NexaStay source and prepaid stays',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Do not promote a broken funnel',
        'Mystery-shop search → checkout → confirmation before running ads or QR codes to the site.'
      ),
    ],
    steps: [
      headingSteps(),
    ],
    screens: [
      bullets(
        [
          'Hotel landing — brand, photos, call-to-search',
          'Search results — available rooms/rates for dates',
          'Checkout — guest details, stay summary, pay if required',
          'Payment — gateway flow when prepaid',
          'Confirmation — booking reference for guest and PMS',
          'Guest profile / bookings — review or cancel per policy',
          'About / Contact / Terms — trust and policy pages',
        ],
        'Guest-facing surfaces'
      ),
      table(
        ['Guest action', 'Hotel system effect', 'FO follow-up'],
        [
          ['Completes paid checkout', 'PMS reservation created', 'Honour prepaid; prepare room'],
          ['Cancels within policy', 'Booking status updates per rules', 'Do not charge wrongly'],
          ['Arrives without printing voucher', 'Find by name/mobile/code', 'Check in from PMS'],
        ],
        'Guest → desk bridge'
      ),
    ],
    workflow: [
      workflow('Guest books direct', [
        'Open NexaStay site',
        'Select dates',
        'Choose room/rate',
        'Checkout details',
        'Pay if required',
        'Receive confirmation',
        'Arrive → FO check-in',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Successful checkout creates a PMS reservation subject to availability and rate configuration at booking time. Inventory for those dates is held like other channel/direct bookings once confirmed.',
        'Cancellations follow configured policy on the guest bookings pages. Coupons apply when enabled in your NexaStay setup. Payment failures stop confirmation — guests should not be treated as booked until PMS shows the reservation.',
        'Google/OAuth login issues are configuration problems on the public site, not FO calendar bugs. Escalate setup issues to whoever owns Distribution → NexaStay and payment keys.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'FO readiness',
        'Train FO to recognise NexaStay as a booking source and honour prepaid flags.'
      ),
      callout(
        'tip',
        'Mystery shop monthly',
        'Book a refundable test stay off-peak to verify photos, rates, payment, and PMS arrival.'
      ),
      callout(
        'note',
        'QR restaurant ≠ NexaStay',
        'Table QR ordering is POS. NexaStay is room booking.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep Manage Rate loaded for all public sell dates.',
        'Update Terms pages when deposit/cancellation rules change.',
        'Match door room types to public room names to avoid desk arguments.',
        'Monitor Booking Dashboard/source mix for direct share after campaigns.',
        'Ensure confirmation emails/WhatsApp paths work when Communications is enabled.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Website button pointing to an old URL.',
        'Promoting direct rates not loaded in Manage Rate.',
        'FO recharging a guest who already prepaid on NexaStay.',
        'Launching ads before payment gateway keys are live.',
        'Leaving expired promo terms on the public Terms page.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Payment failure',
            'Gateway keys/network/bank decline',
            'Verify gateway config; ask guest to retry; check PMS if partial create occurred',
          ],
          [
            'Booking missing in PMS',
            'Checkout not completed or sync issue',
            'Confirm with guest email/time; contact support with details',
          ],
          [
            'Google login issues',
            'OAuth config',
            'Fix NexaStay auth settings with implementer/admin',
          ],
          [
            'Rate shows wrong',
            'Manage Rate / plan mismatch',
            'Correct Manage Rate and re-test search',
          ],
          [
            'Guest cancelled but FO still expects arrival',
            'Status not refreshed / policy confusion',
            'Check booking status in PMS; honour policy',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is QR restaurant part of NexaStay?',
          a: 'No — QR ordering is POS. NexaStay is rooms.',
        },
        {
          q: 'Who configures NexaStay?',
          a: 'Managers with Distribution / NexaStay setup access. See nexastay-setup.',
        },
        {
          q: 'Do guests need a RoomNexa staff login?',
          a: 'No. Guests use the public site and optional guest profile — staff use the PMS.',
        },
        {
          q: 'Can FO edit a NexaStay booking?',
          a: 'Yes via Booking List/Detail like other reservations, within your policy and permissions.',
        },
        {
          q: 'What if the guest arrives without confirmation?',
          a: 'Search Booking List by name, mobile, or dates; verify identity; then check in.',
        },
      ]),
    ],
  }),
];

function headingSteps() {
  return steps([
    {
      text: 'Guest opens your NexaStay hotel landing page (from your marketing link or QR to the rooms site).',
    },
    {
      text: 'Selects check-in/out dates and searches available rooms/rates.',
    },
    {
      text: 'Chooses a room and rate plan, reviews the stay summary, and continues to checkout.',
    },
    {
      text: 'Enters guest details (name, contact, required fields) and applies coupon codes if offered.',
    },
    {
      text: 'Pays via the configured gateway when prepaid is required; waits for payment success.',
    },
    {
      text: 'Receives confirmation with reference; booking appears in the hotel PMS for front office.',
    },
    {
      text: 'Guest may use profile/bookings pages later to review or cancel within policy.',
    },
    {
      text: 'On arrival, FO finds the reservation, verifies prepaid/status, assigns a clean room, and checks in.',
    },
  ]);
}

export const referenceGuides = [
  buildGuide({
    id: 'glossary',
    title: 'Glossary & Terminology',
    moduleId: 'reference',
    summary: 'Common PMS terms used across RoomNexa documentation.',
    keywords: ['glossary', 'terminology', 'definitions', 'adr', 'revpar', 'folio'],
    pathHint: 'User Guide → Reference',
    related: ['welcome', 'booking-overview'],
    overview: [
      ...paragraphs(
        'Hotels mix industry jargon with RoomNexa screen names. This glossary aligns both so SOPs, shift briefings, and the User Guide use the same words.',
        'New joiners should skim it during induction week. Supervisors should correct slang that causes errors (for example calling stock “inventory” when they mean sellable rooms).',
        'When a term appears on a screen you do not recognise, use User Guide Search for the screen name, then return here for the short definition.',
        'Example: a trainee hears “mark 204 OOO” — glossary points to Out of order; Housekeeping guides explain how that blocks FO assignment.'
      ),
      table(
        ['Term', 'Meaning'],
        [
          ['PMS', 'Property Management System — software running hotel operations'],
          ['OTA', 'Online Travel Agency (Booking.com, etc.)'],
          ['Walk-in', 'Guest arrives without prior reservation'],
          ['Folio', 'Guest bill / account of charges'],
          ['ADR', 'Average Daily Rate'],
          ['RevPAR', 'Revenue per available room (revenue ÷ available rooms)'],
          ['Occupancy', 'Share of rooms sold'],
          ['No show', 'Reserved guest did not arrive'],
          ['OOO', 'Out of order room'],
          ['KOT', 'Kitchen Order Ticket'],
          ['Allotment', 'Inventory given to a channel'],
          ['RBAC', 'Role-based access control'],
          ['NexaStay', 'RoomNexa direct booking engine'],
          ['ChannelHub', 'Sync engine for channel events/webhooks'],
          ['Stock', 'Consumable goods inventory (not rooms)'],
          ['Hotelier', 'Landing screen to select which hotel to operate'],
          ['Quick Book', 'Dashboard drawer to create a booking fast from the calendar'],
          ['Pending Tasks', 'Operational follow-ups (check-in/out, HK, etc.) on Dashboard'],
        ]
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        ['None', 'Reference content — readable by any logged-in staff with User Guide access'],
      ]),
      bullets(
        ['Optional: print for induction packs', 'Use Search when a term appears mid-shift'],
        'Prerequisites'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Glossary when you hear an unfamiliar term in briefing or on a screen.',
        },
        {
          text: 'Find the term in the table and read the RoomNexa-oriented meaning.',
        },
        {
          text: 'Use User Guide Search or Related Guides to open the feature page that uses the term.',
        },
        {
          text: 'Practice once on the real screen (demo date if needed) so the word sticks.',
        },
        {
          text: 'Add the same term to your property SOP so teams do not invent synonyms.',
        },
        {
          text: 'Ask your supervisor if a local policy term is missing — escalate to update training materials.',
        },
      ]),
    ],
    screens: [
      paragraphs(
        'This page is a reference table inside the User Guide. It is not an operational module with Save buttons.'
      ),
      bullets(
        [
          'Term column — word you will hear in FO/HK/F&B/revenue conversations',
          'Meaning column — short definition aligned to RoomNexa usage',
          'Search — jump from glossary words into deeper guides',
        ],
        'How to read it'
      ),
    ],
    workflow: [
      workflow('Learn a term', [
        'Hear/see unknown word',
        'Find in glossary',
        'Open related guide',
        'Practice on screen',
        'Use in SOP language',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Glossary definitions are descriptive, not billing engines. ADR/occupancy calculations on dashboards follow each dashboard’s statistics rules — use Performance/Reports guides for how numbers are built.',
        'RoomNexa-specific names (NexaStay, ChannelHub, Hotelier, Quick Book) refer to product surfaces in this deployment. Prefer those names in tickets to support so routing is faster.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Teach new joiners',
        'Print this glossary for induction week.'
      ),
      callout(
        'tip',
        'One word per concept',
        'Pick “OOO” or “out of order” and stick to it on radio and in notes.'
      ),
      callout(
        'note',
        'Stock ≠ rooms',
        'Stock module is consumables. Sellable rooms are inventory/rooms configuration.'
      ),
    ],
    bestPractices: [
      bullets([
        'Use the same terms in SOPs and RoomNexa notes.',
        'Correct ambiguous slang during handover (“inventory” → rooms or stock).',
        'Link glossary terms to Related Guides in training decks.',
        'Keep a laminated cheat sheet at the desk for ADR/occupancy/no-show.',
        'When filing support tickets, use screen names from this guide set.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Calling Stock “inventory” when you mean rooms — be specific.',
        'Confusing Organisation Dashboard with Performance Dashboard.',
        'Treating NexaStay and POS QR as the same guest app.',
        'Using ADR casually without matching the dashboard date range.',
        'Writing unclear notes (“fix issue”) instead of OOO/maintenance.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Confused term in UI',
            'Industry vs RoomNexa naming',
            'Search User Guide for the screen name',
          ],
          [
            'Missing term',
            'Local SOP slang',
            'Ask supervisor; use Search; request glossary update',
          ],
          [
            'Two teams disagree on meaning',
            'No shared vocabulary',
            'Adopt this glossary in briefing for a week',
          ],
          [
            'Support ticket misunderstood',
            'Vague wording',
            'Rewrite using glossary + screen path hints',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Missing term?',
          a: 'Use Search; if still unclear, ask your supervisor or support.',
        },
        {
          q: 'Is RevPAR shown everywhere?',
          a: 'Not every chart labels RevPAR. Use Performance/Reports metrics available on your package and compute carefully if needed.',
        },
        {
          q: 'What is a folio in RoomNexa?',
          a: 'The guest’s running bill of room, extras, POS room charges, taxes, and payments — settled at checkout.',
        },
        {
          q: 'OOO vs dirty?',
          a: 'Dirty needs cleaning; OOO/maintenance means do not sell until cleared.',
        },
        {
          q: 'Can I suggest new glossary rows?',
          a: 'Yes via your GM/training owner — keep definitions tied to real RoomNexa screens.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'system-requirements',
    title: 'System Requirements',
    moduleId: 'reference',
    summary: 'Browsers, devices, and network basics for a smooth RoomNexa experience.',
    keywords: ['system requirements', 'browser', 'mobile', 'network'],
    pathHint: 'User Guide → Reference',
    related: ['first-login', 'troubleshooting-global'],
    overview: [
      ...paragraphs(
        'RoomNexa is a cloud web application. Front-desk reliability depends on browser health, network stability, and sensible device choices more than on any single “install package”.',
        'Use modern Chrome, Edge, Safari, or Firefox (latest two versions recommended). Desktop or laptop remains best for heavy configuration, rate grids, and PDF exports; mobile/tablet shells support many operational views for roaming staff.',
        'Unstable Wi-Fi, captive portals, and aggressive browser extensions cause the most mysterious failures: blank pages, stuck uploads, blocked invoice pop-ups, and interrupted Dashboard refreshes.',
        'Example: a property upgrades an ancient PC browser “next month” while ID uploads fail all weekend. Updating the browser and allowing camera/file permissions fixes more guest friction than restarting the router alone.',
        'IT should whitelist RoomNexa domains and avoid intercepting websockets/uploads on guest or staff VLANs used by the PMS.'
      ),
      bullets(
        [
          'Modern Chrome, Edge, Safari, or Firefox (latest two versions recommended)',
          'Stable broadband; avoid captive portals that break websockets/uploads',
          'Desktop for heavy configuration; mobile supported for many ops views',
          'Allow pop-ups for PDF invoices and dashboard PDF exports if blocked',
          'Camera/file permissions when uploading IDs/photos',
        ],
        'Baseline checklist'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'IT / local admin on staff PCs',
          'Needed to update browsers, whitelist domains, and manage extensions',
        ],
      ]),
      bullets(
        [
          'Official RoomNexa URL bookmarked',
          'Secondary connectivity plan (4G hotspot) for outages',
          'UPS for front-desk PC if power is unreliable',
        ],
        'Prerequisites'
      ),
      callout(
        'important',
        'Network lockdowns',
        'If your corporate network is locked down, IT must whitelist RoomNexa domains before blaming the PMS.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Update the staff browser to a current stable release; remove unused profiles bloated with personal extensions on shared PCs.',
        },
        {
          text: 'Create a clean staff browser profile without ad blockers that break uploads or scripts.',
        },
        {
          text: 'Login, open a hotel, and test Dashboard calendar load on the primary front-desk PC.',
        },
        {
          text: 'Test PDF invoice/report download; allow pop-ups for RoomNexa when the browser blocks them.',
        },
        {
          text: 'Test ID/photo upload with camera/file permissions granted to the site.',
        },
        {
          text: 'On mobile devices used by HK/FO, confirm the installed/app shell or mobile browser path your deployment provides.',
        },
        {
          text: 'Document the hotspot failover: who turns it on, which SSID, and how staff reconnect sessions.',
        },
        {
          text: 'Retest after any Windows/macOS major update — browsers sometimes reset pop-up and permission prompts.',
        },
      ]),
    ],
    screens: [
      paragraphs(
        'N/A as a product module — this is an environment checklist. Failures usually appear as blank pages, upload errors, or blocked PDFs inside normal RoomNexa screens.'
      ),
      table(
        ['Symptom on screen', 'Environment angle', 'First check'],
        [
          ['Blank Landing/Dashboard', 'Cache, old browser, network', 'Hard refresh; try second browser'],
          ['Upload fails', 'File size, permissions, captive portal', 'Smaller image; grant camera/files; stable Wi-Fi'],
          ['PDF never downloads', 'Pop-up blocker', 'Allow pop-ups for RoomNexa'],
          ['Mobile layout odd', 'Unsupported webview', 'Use supported shell/browser'],
        ],
        'Environment vs product'
      ),
    ],
    workflow: [
      workflow('New PC setup', [
        'Install/update browser',
        'Clean staff profile',
        'Login',
        'Open hotel',
        'Test calendar',
        'Test PDF',
        'Test ID upload',
        'Document failover',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'The client-side app talks to RoomNexa APIs over the network. There is no full offline PMS mode that replaces live cloud operations — treat connectivity as part of the product.',
        'Pop-up blockers and permission prompts are browser security features. RoomNexa PDF and upload flows depend on them being correctly allowed for your official origin.',
        'Extensions that rewrite pages (ad blockers, script blockers) can break booking drawers and file inputs without showing a clear RoomNexa error.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Front desk PC',
        'Keep a clean browser profile for staff without personal extensions.'
      ),
      callout(
        'tip',
        'Two browsers installed',
        'Keep a second browser as fallback when the primary profile corrupts after an OS update.'
      ),
      callout(
        'note',
        'Guest Wi-Fi ≠ staff Wi-Fi',
        'Do not run the PMS on the captive guest portal SSID.'
      ),
    ],
    bestPractices: [
      bullets([
        'UPS for front desk PC.',
        'Secondary 4G hotspot for outages.',
        'Quarterly browser update day for all ops PCs.',
        'Whitelist RoomNexa in endpoint security tools.',
        'Train night staff how to allow pop-ups without calling IT at 1 AM.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Ancient browsers on property PCs.',
        'Running FO on guest Wi-Fi with a sign-in interstitial.',
        'Blocking all pop-ups globally “for security”.',
        'Personal Chrome profiles full of coupons extensions on the desk PC.',
        'Assuming mobile can replace desktop for tax/rate configuration work.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Blank page',
            'Cache, extension, or old browser',
            'Hard refresh; clear cache; try another browser; disable extensions',
          ],
          [
            'Upload fails',
            'File size/network/permissions',
            'Compress image; check network; grant file/camera access',
          ],
          [
            'PDF blocked',
            'Pop-up blocker',
            'Allow pop-ups; retry export/download',
          ],
          [
            'Intermittent saves',
            'Unstable Wi-Fi',
            'Move to staff SSID; use hotspot failover',
          ],
          [
            'Mobile cannot configure rates',
            'Viewport/tooling limits',
            'Use desktop for Manage Rate and Configuration',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Internet mandatory?',
          a: 'Yes for live RoomNexa cloud operations.',
        },
        {
          q: 'Which browser is best?',
          a: 'Any current Chrome/Edge/Safari/Firefox. Standardise the property on one primary + one backup.',
        },
        {
          q: 'Can we use Internet Explorer?',
          a: 'No — use a modern supported browser.',
        },
        {
          q: 'Do we need a native desktop install?',
          a: 'RoomNexa runs in the browser (and mobile shell where provided). Follow your deployment’s official URL.',
        },
        {
          q: 'Are tablets OK for check-in?',
          a: 'Yes for many ops views; keep a desktop for heavy configuration and large PDF packs.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'troubleshooting-global',
    title: 'Troubleshooting & FAQs',
    moduleId: 'reference',
    summary: 'Cross-module problems staff hit most often and how to resolve them.',
    keywords: ['troubleshooting', 'errors', 'faq', 'help', 'not working'],
    pathHint: 'User Guide → Reference',
    related: ['users-roles', 'system-requirements', 'support'],
    overview: [
      ...paragraphs(
        'Most “RoomNexa is broken” moments are configuration, permissions, availability, or browser issues. This page is the cross-module first-aid kit before you escalate.',
        'Start by reproducing once, confirming hotel context, and checking the Related Guide for the exact screen. Then verify role permissions and package entitlements.',
        'Gather evidence early: hotel name, username, approximate time, screenshot, booking/order codes. Support resolves precise tickets far faster than “not working”.',
        'Example: FO cannot assign a room — first read Housekeeping Room Status (dirty/OOO), then Booking allocation rules, then permissions. Creating a second booking “to test” on live sold-out dates usually makes it worse.'
      ),
      table(
        ['Symptom', 'Try this'],
        [
          ['Menu item missing', 'Permissions/package — ask admin'],
          ['Wrong hotel data', 'Confirm hotel context on header / Hotelier'],
          ['Cannot save booking', 'Required fields + availability'],
          ['Rate shows 0', 'Manage Rate for dates/plan'],
          ['Room not assignable', 'HK status / OOO / allocation'],
          ['WhatsApp not sending', 'Communications + phone format'],
          ['OTA mismatch', 'Distribution mapping/inventory sync'],
          ['PDF blocked', 'Allow pop-ups'],
          ['Login issues', 'Reset password; check user active'],
        ]
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Supervisor escalation path',
          'Know who can change roles, rates, and HK status on your shift',
        ],
      ]),
      bullets(
        [
          'Hotel name, username, approximate time, screenshot, booking/order code',
          'Whether the issue is one user or all users',
          'Browser name/version if pages look blank',
        ],
        'Gather before escalating'
      ),
      callout(
        'warning',
        'Do not duplicate live bookings',
        'Avoid creating duplicate bookings while “testing” on live high-occupancy dates.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Reproduce once carefully. Note the exact screen, button, and error text.',
        },
        {
          text: 'Confirm hotel header — switch via Hotelier if wrong.',
        },
        {
          text: 'Open the Related Guide for that screen and check Before You Begin permissions.',
        },
        {
          text: 'Verify role permissions and hotel package with admin if a menu or action is missing.',
        },
        {
          text: 'Check environment basics (browser, pop-ups, network) using System Requirements.',
        },
        {
          text: 'Apply the smallest fix (one variable). Re-test.',
        },
        {
          text: 'If still blocked, escalate via Support with evidence. Stop repeating failed saves.',
        },
        {
          text: 'After resolution, note the cause in handover so the next shift does not repeat it.',
        },
      ]),
    ],
    screens: [
      paragraphs(
        'Use in-app Support chat FAB if available on your deployment. Otherwise follow your organisation’s phone/email escalation path.'
      ),
      bullets(
        [
          'Error toasts/dialogs — copy text into the ticket',
          'Header hotel name — first context check',
          'User Guide Search — find the screen-specific troubleshooting table',
        ],
        'Where clues live'
      ),
    ],
    workflow: [
      workflow('Incident', [
        'Detect',
        'Confirm hotel context',
        'Reproduce once',
        'Check guide + permissions',
        'Contain (stop repeating)',
        'Fix or escalate',
        'Document',
        'Prevent',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'RoomNexa enforces availability, RBAC, and package rules on purpose. A blocked save often protects inventory or audit integrity — treat the message as information, not noise.',
        'Dashboards and lists can be briefly cached; refresh after another user changes HK status or bookings before assuming desync.',
        'Channel/OTA mismatches are usually mapping or allotment configuration, not calendar cosmetics — involve Distribution owners early.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'One change',
        'When testing fixes, change one variable at a time.'
      ),
      callout(
        'tip',
        'Screenshot the toast',
        'Error text disappears — capture it before clicking away.'
      ),
      callout(
        'note',
        'Guest waiting',
        'If a guest is at the desk, switch to a manual goodwill path while IT investigates — do not argue with the screen in front of them.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep a front-desk escalation tree (FO lead → GM → support).',
        'Never share passwords in tickets or chat.',
        'Prefer Related Guides over random clicking through menus.',
        'Log recurring issues weekly for training (rates zero, pop-ups, wrong hotel).',
        'After major config changes, mystery-shop one booking path.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating duplicate bookings while “testing” on live dates.',
        'Escalating without hotel name or booking code.',
        'Granting owner permissions to “make the menu appear”.',
        'Ignoring HK dirty/OOO when rooms “will not assign”.',
        'Blaming NexaStay when POS QR is the broken link (or vice versa).',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Still stuck after guide steps',
            'Needs platform support',
            'Contact support with booking code + screenshots + time',
          ],
          [
            'Only one user affected',
            'Role/hotel assignment',
            'Compare permissions with a working colleague',
          ],
          [
            'All users affected',
            'Package, outage, or config',
            'Check subscription; status page/support; recent config changes',
          ],
          [
            'Works on phone not PC',
            'Browser/extension on PC',
            'Clean profile; second browser; see System Requirements',
          ],
          [
            'Numbers look wrong',
            'Date filter / hotel scope',
            'Match ranges; confirm Organisation vs property dashboard',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where is keyboard shortcuts list?',
          a: 'RoomNexa is primarily pointer-driven; use browser find and in-guide Search. Property-specific shortcut overlays are not a core documented feature.',
        },
        {
          q: 'Night audit button?',
          a: 'Run operational close via Cash Counter close + key Reports exports as your night pack.',
        },
        {
          q: 'Backup & restore?',
          a: 'Cloud data stewardship is handled by RoomNexa platform operations — not a staff self-serve restore screen.',
        },
        {
          q: 'Why did a menu disappear overnight?',
          a: 'Role change, package/entitlement change, or hotel feature flags. Ask admin to review users and subscription.',
        },
        {
          q: 'Should I restart the router first?',
          a: 'If multiple cloud tools fail, yes. If only one RoomNexa action fails with a clear validation error, fix the data/permission instead.',
        },
        {
          q: 'Can support see my screen?',
          a: 'Only through whatever remote tools your organisation authorises — never send passwords.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'support',
    title: 'Getting Support',
    moduleId: 'reference',
    summary: 'How to escalate issues with the right details for faster resolution.',
    keywords: ['support', 'helpdesk', 'complaint', 'chat'],
    pathHint: 'In-app Support FAB / your admin channel',
    related: ['troubleshooting-global', 'welcome'],
    overview: [
      ...paragraphs(
        'When guides and supervisors cannot unblock you, escalate with precise context. Many deployments include an in-app support/complaint chat FAB (often bottom-right).',
        'Good tickets include hotel name, user id, screen path, button clicked, error text, time, and booking/order codes. Vague “not working” tickets take longer and hurt guests waiting at the desk.',
        'Support is not a substitute for training or SOP. Use the User Guide first for how-to questions; use Support for defects, outages, and entitlement issues you cannot resolve locally.',
        'Example: payment link fails for one booking — FO captures the error toast, booking code, guest mobile, and time, sends via FAB, then collects cash temporarily while waiting so the queue keeps moving.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Try Troubleshooting guide first',
          'Many issues are permissions, rates, or HK status — fixable without Support',
        ],
      ]),
      bullets(
        [
          'Screenshot ready (include error text)',
          'Booking/order codes',
          'User id / role',
          'Hotel name and approximate time of issue',
          'Steps already tried',
        ],
        'Have ready'
      ),
      callout(
        'warning',
        'Never send passwords',
        'Do not paste passwords, OTPs, or full card numbers into chat tickets.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Confirm hotel header and logged-in user. Reproduce once more if safe.',
        },
        {
          text: 'Note exact screen path and the button/field that failed.',
        },
        {
          text: 'Capture error text and a screenshot. Copy booking/order codes.',
        },
        {
          text: 'Check Troubleshooting & FAQs and the screen’s own troubleshooting table.',
        },
        {
          text: 'Contact support via FAB or your organisation’s channel with the evidence pack.',
        },
        {
          text: 'While waiting, protect the guest experience (alternate payment, manual note) without creating duplicate live bookings.',
        },
        {
          text: 'Apply the fix when instructed; confirm with a retest.',
        },
        {
          text: 'Update the ticket when resolved and brief the next shift if the issue could recur.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Support chat FAB — bottom-right on many screens (deployment-dependent)',
          'Ticket thread — keep one incident per thread',
          'Fallback — admin phone/email escalation if FAB missing',
        ],
        'Support entry points'
      ),
      fieldTable([
        ['Hotel name', 'Yes', 'Routes the issue to the right property context'],
        ['Booking/order code', 'When relevant', 'Lets support find the record quickly'],
        ['Screenshot / error text', 'Yes', 'Shows the failure mode'],
        ['User id', 'Yes', 'Checks permissions and audit'],
        ['Time of issue', 'Yes', 'Correlates with logs'],
      ]),
    ],
    workflow: [
      workflow('Escalate', [
        'Gather facts',
        'Self-check guide',
        'Contact support',
        'Contain guest impact',
        'Apply fix',
        'Confirm with guest',
        'Close ticket',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Support needs reproducibility. Tickets without hotel/user/time force slow back-and-forth and delay guests.',
        'Some issues are entitlement/billing (expired subscription) — those may route through Settings/billing contacts rather than pure product bugs.',
        'Creating duplicate bookings or voiding records while waiting often creates a second incident. Prefer containment that preserves a single source of truth.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Guest-facing calm',
        'If a guest is waiting, switch to a manual goodwill process while IT investigates.'
      ),
      callout(
        'tip',
        'One thread',
        'Do not open five chats for the same booking — it splits context.'
      ),
      callout(
        'note',
        'Training vs defect',
        '“How do I check in?” belongs in User Guide. “Check-in button errors after Save” belongs in Support.'
      ),
    ],
    bestPractices: [
      bullets([
        'One ticket per incident thread.',
        'Update ticket when resolved.',
        'Include “steps already tried” to avoid ping-pong.',
        'Keep a property WhatsApp/email bridge only for urgency — still file a proper ticket.',
        'Never share passwords or OTPs in chat.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Sending passwords in chat.',
        'Omitting booking codes.',
        'Opening duplicate tickets from three staff for one outage.',
        'Continuing to hammer Save and creating duplicates.',
        'Escalating missing menus without asking admin about roles first.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'FAB missing',
            'Deployment without chat widget',
            'Use admin phone/email escalation path',
          ],
          [
            'No reply yet',
            'Incomplete ticket or queue volume',
            'Add missing evidence; use urgent path for guest-blocking outages',
          ],
          [
            'Wrong hotel investigated',
            'Header context not stated',
            'Resend with hotel name + booking code',
          ],
          [
            'Issue returned after “fix”',
            'Partial fix or cache',
            'Retest; reopen ticket with new screenshots',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Training request?',
          a: 'Use this User Guide first; ask your GM for floor training on property SOPs.',
        },
        {
          q: 'What is the fastest ticket?',
          a: 'Hotel + user + time + screenshot + booking code + steps tried.',
        },
        {
          q: 'Can I call instead of chat?',
          a: 'Follow your organisation’s published support channels — many teams still want a written ticket for tracking.',
        },
        {
          q: 'Who renews subscription?',
          a: 'Org admin/billing via Settings → Subscription; Support can advise but payment authority sits with your organisation.',
        },
        {
          q: 'Should guests email Support?',
          a: 'No — guests go through the hotel. Staff escalate with PMS references.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'best-practices-global',
    title: 'Hotel Best Practices',
    moduleId: 'reference',
    summary: 'Cross-functional habits that keep RoomNexa accurate and guests happy.',
    keywords: ['best practices', 'sop', 'front office habits'],
    pathHint: 'User Guide → Reference',
    related: ['booking-overview', 'housekeeping-overview', 'cash-counter'],
    overview: [
      ...paragraphs(
        'RoomNexa works best when the hotel runs disciplined habits: search before create, read HK before assign, settle POS before checkout, close cash daily, and keep roles least-privilege.',
        'These practices are cross-module. They prevent the expensive classes of error — duplicate guests, dirty-room check-ins, unpaid restaurant bills at departure, and unauditable shared logins.',
        'Adapt them into your property SOP. Consistency beats perfection: three enforced habits beat fifteen ignored posters.',
        'Example: a tight city hotel briefs every shift — Dashboard first, Tasks clear, HK board check, no checkout with open POS, cash close before handover. Complaint rates drop because the system stays truthful.'
      ),
      bullets(
        [
          'Search before create — avoid duplicate guests/bookings',
          'Read HK board before assigning rooms',
          'Settle POS before checkout',
          'Close cash daily',
          'Keep mobile numbers accurate',
          'Least-privilege roles',
          'Verify OTA maps after any room type change',
          'Mystery-shop NexaStay and QR menus',
        ],
        'Core habits'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Supervisor buy-in',
          'Habits stick only when leads coach them on shift',
        ],
      ]),
      bullets(
        [
          'Pick three practices to enforce this week',
          'Agree who audits tomorrow’s compliance',
          'Link each habit to a User Guide page for training',
        ],
        'Prerequisites'
      ),
      callout(
        'note',
        'Local SOP wins',
        'Adapt these to your property SOP; consistency beats perfection.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Pick three practices to enforce this week (for example Dashboard start, HK before assign, cash close).',
        },
        {
          text: 'Brief every shift with the three habits and the “why” (guest impact + audit).',
        },
        {
          text: 'Post the habits near the desk and link the related User Guide pages.',
        },
        {
          text: 'Spot-check mid-week: sample five check-ins for HK status, five checkouts for POS clear.',
        },
        {
          text: 'Coach privately when someone slips; praise clean audits publicly.',
        },
        {
          text: 'Add the next three habits only after the first set sticks.',
        },
        {
          text: 'Review quarterly with RBAC, Distribution mapping, and mystery-shop results.',
        },
        {
          text: 'Update SOP language to match RoomNexa terms from the Glossary.',
        },
      ]),
    ],
    screens: [
      paragraphs(
        'Applies across modules — Home Dashboard, Housekeeping, POS, Cash Counter, Distribution, Users. There is no single “best practices” button; habits show up as clean data and calm shifts.'
      ),
      table(
        ['Habit', 'Primary screens', 'Failure signal'],
        [
          ['Search before create', 'Customer List / Booking List', 'Duplicate guests'],
          ['HK before assign', 'Room Status / Tasks', 'Dirty-room complaints'],
          ['POS before checkout', 'Restaurant orders / Checkout', 'Surprise bills at departure'],
          ['Cash close daily', 'Cash Counter', 'Drawer variance'],
          ['Least privilege', 'Users & Roles', 'Unauditable refunds'],
        ],
        'Habit → screen map'
      ),
    ],
    workflow: [
      workflow('Shift excellence', [
        'Login as yourself',
        'Open correct hotel',
        'Dashboard + Tasks',
        'HK board',
        'Arrivals / allocation',
        'POS discipline',
        'Checkout & cash',
        'Handover notes',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Data quality compounds — bad inputs create bad reports, bad channel allotments, and bad guest experiences. Dashboards cannot fix undisciplined posting.',
        'RBAC and package entitlements encode who may break rules (discounts, voids). Best practice is to keep those powers rare and audited.',
        'Distribution and room type changes have side effects on OTAs. A “small rename” without remapping becomes a week of availability tickets.'
      ),
    ],
    tips: [
      callout(
        'success',
        'Handover note',
        'Write three bullets for the next shift inside a shared SOP doc.'
      ),
      callout(
        'tip',
        'Start small',
        'Three habits this week beat a laminated poster of twenty ignored rules.'
      ),
      callout(
        'note',
        'Mystery shop',
        'Book your own NexaStay and scan a QR menu quarterly — catch guest pain before reviews do.'
      ),
    ],
    bestPractices: [
      bullets([
        'Celebrate clean audits.',
        'Coach privately; process publicly.',
        'Keep named logins — never permanent shared FO accounts.',
        'Reconcile cash and POS room charges before night close.',
        'Re-verify channel mapping after room type or rate plan structural changes.',
        'Train new staff Welcome → First Login → Booking Overview → Walk-in → Check-in → Checkout.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Tribal knowledge never written down.',
        'Enforcing twenty rules at once until staff ignore all of them.',
        'Skipping HK board on busy arrivals.',
        'Leaving POS bills open until the guest has left the lobby.',
        'Promoting NexaStay while Manage Rate is empty for peak dates.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'SOP ignored',
            'Too long / uncoached',
            'Simplify SOP; train; spot check',
          ],
          [
            'Same error every weekend',
            'Habit not briefed on relief shifts',
            'Add to relief checklist; verify named logins',
          ],
          [
            'Reports look “wrong”',
            'Sloppy posting all week',
            'Retrain search-before-create and payment method discipline',
          ],
          [
            'Channel complaints after rename',
            'Mapping not updated',
            'Distribution remapping + allotment check',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Where to start new staff?',
          a: 'Welcome → First Login → Property Setup (managers) → Booking Overview → Walk-in → Check-in → Checkout.',
        },
        {
          q: 'How many habits should we roll out?',
          a: 'Three per week until stable. Add more only after audits look clean.',
        },
        {
          q: 'Who owns best practices?',
          a: 'GM/FO manager. RoomNexa provides tools; the hotel enforces habits.',
        },
        {
          q: 'Do these replace module guides?',
          a: 'No — they sit on top. Use module guides for button-level steps.',
        },
        {
          q: 'What is the #1 money-saver?',
          a: 'Correct hotel context + HK-before-assign + POS settled before checkout.',
        },
      ]),
    ],
  }),
];
